/**
 * Per-class toast treatment.
 *
 * ONE display mapping shared by the app-wide `NotificationToastDispatcher` and
 * the `NotificationToast` functional story — there is no second toast pipeline.
 * Each class gets a visually + semantically distinct `sonner` variant so a
 * reader (and a test) can tell an info from a success from an error from a
 * critical by ROLE/LABEL, not colour alone:
 *
 *   - notification `info`     → neutral `toast()`      (data-type absent)
 *   - notification `success`  → `toast.success()`      (data-type="success")
 *   - error (severity error)  → `toast.error()`        (data-type="error")
 *   - error (severity critical) → `toast.error()` with an explicit "Critical:"
 *     label so the highest-severity class is distinguishable from a plain error.
 *
 * This module is pure logic (no React component) so it can live in a `.ts` file
 * without breaking Vite Fast Refresh for the `.tsx` dispatcher/stories that
 * import it (reference_react_noncomponent_export_breaks_hmr).
 */

import { toast } from '@localagent/ui/components/sonner';
import type { NotificationEventFrame, ErrorEventFrame } from '@localagent/sdk';

export type ToastClass = 'info' | 'success' | 'warning' | 'error' | 'critical';

export function errorToastClass(severity: string): ToastClass {
  return severity === 'critical' ? 'critical' : 'error';
}

export function showNotificationToast(
  frame: { kind: ToastClass } & Pick<NotificationEventFrame, 'title' | 'body'> & {
    action?: { label: string; onClick: () => void };
  },
): void {
  const description = frame.body && frame.body !== frame.title ? frame.body : undefined;
  const opts = { description, action: frame.action };
  if (frame.kind === 'success') {
    toast.success(frame.title, opts);
  } else if (frame.kind === 'warning') {
    toast.warning(frame.title, opts);
  } else {
    toast(frame.title, opts);
  }
}

export function showErrorToast(
  frame: Pick<ErrorEventFrame, 'severity' | 'service_origin'>,
  resolvedMessage: string,
): void {
  const cls = errorToastClass(frame.severity);
  const title = cls === 'critical' ? `Critical: ${resolvedMessage}` : resolvedMessage;
  toast.error(title, { description: frame.service_origin || undefined });
}
